import React, { useCallback, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import clsx from 'clsx'

import { Button } from '@/components/Button/Button'
import { LoginHeader } from '@/components/widget-components'
import { ROUTES } from '@/constants/corporateRoutePaths'
import {
  addAdditionalCorporateDocuments,
  uploadCorporateDocuments,
} from '@/requests/corporate'
import { CorporatePageLayout } from './CorporatePageLayout'

const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png']
const MAX_FILE_BYTES = 10 * 1024 * 1024
const MAX_FILES = 5

type FileError = 'invalidType' | 'tooLarge' | 'tooMany'

const findFileError = (files: File[]): FileError | null => {
  if (files.length > MAX_FILES) return 'tooMany'
  if (files.some((file) => !ALLOWED_TYPES.includes(file.type))) {
    return 'invalidType'
  }
  if (files.some((file) => file.size > MAX_FILE_BYTES)) return 'tooLarge'
  return null
}

const sendDocuments = async (files: File[]) => {
  const objectNames = await uploadCorporateDocuments(
    files.map((file) => ({ file, name: file.name, contentType: file.type }))
  )
  await addAdditionalCorporateDocuments(objectNames)
}

export const CorporateAdditionalDocumentsPage = React.memo(
  function CorporateAdditionalDocumentsPage() {
    const { t } = useTranslation()
    const inputRef = useRef<HTMLInputElement>(null)
    const [files, setFiles] = useState<File[]>([])
    const [fileError, setFileError] = useState<FileError | null>(null)
    const [isDragging, setIsDragging] = useState(false)

    const {
      mutate: send,
      isPending,
      isSuccess,
      isError,
      reset,
    } = useMutation({
      mutationFn: sendDocuments,
      onSuccess: () => setFiles([]),
    })

    const addFiles = useCallback(
      (picked: File[]) => {
        reset()
        const next = [...files, ...picked]
        const error = findFileError(next)
        setFileError(error)
        if (!error) setFiles(next)
      },
      [files, reset]
    )

    const removeFile = useCallback((index: number) => {
      setFileError(null)
      setFiles((current) => current.filter((_, i) => i !== index))
    }, [])

    const handleSelect = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        addFiles(Array.from(event.target.files ?? []))
        event.target.value = ''
      },
      [addFiles]
    )

    const handleDrop = useCallback(
      (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        setIsDragging(false)
        addFiles(Array.from(event.dataTransfer.files))
      },
      [addFiles]
    )

    const handleDragOver = useCallback(
      (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        setIsDragging(true)
      },
      []
    )

    const handleDragLeave = useCallback(() => setIsDragging(false), [])

    const openPicker = useCallback(() => inputRef.current?.click(), [])

    const handleSubmit = useCallback(() => send(files), [send, files])

    const hasFiles = files.length > 0

    return (
      <div
        data-testid="container-corporate-additional-documents-page"
        className="flex flex-col md:flex-row gap-4 max-w-[730px] lg:min-w-[730px] mx-auto min-w-full relative"
      >
        <LoginHeader hideBack>{null}</LoginHeader>
        <div className="flex flex-col flex-grow relative">
          <CorporatePageLayout>
            <div
              className="flex flex-col gap-6"
              data-testid="container-corporate-additional-documents"
            >
              <h1 className="text-lg font-normal uppercase">
                {t('corporateAdditionalDocuments.title')}
              </h1>

              <div
                role="button"
                tabIndex={0}
                onClick={openPicker}
                onKeyDown={(event) => event.key === 'Enter' && openPicker()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={clsx(
                  'border-2 border-dashed rounded-lg p-8 flex flex-col items-center justify-center cursor-pointer transition-all',
                  isDragging
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:bg-gray-50',
                  fileError && 'border-red-500'
                )}
                data-testid="container-corporate-additional-documents-upload"
              >
                <input
                  ref={inputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleSelect}
                  accept=".pdf,.jpg,.jpeg,.png"
                  data-testid="input-corporate-additional-documents-file"
                />
                <p className="font-medium">
                  {t('corporateAdditionalDocuments.upload.heading')}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {t('corporateAdditionalDocuments.upload.formatInfo')}
                </p>
              </div>

              {fileError && (
                <span
                  className="text-sm text-red-500"
                  data-testid="error-corporate-additional-documents-file"
                >
                  {t(`corporateAdditionalDocuments.errors.${fileError}`)}
                </span>
              )}

              {hasFiles && (
                <ul
                  className="flex flex-col gap-2"
                  data-testid="list-corporate-additional-documents-files"
                >
                  {files.map((file, index) => (
                    <li
                      key={`${file.name}-${index}`}
                      className="flex items-center justify-between text-sm"
                    >
                      <span className="truncate">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="underline ml-4"
                        disabled={isPending}
                      >
                        {t('corporateAdditionalDocuments.remove')}
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {isSuccess && (
                <p
                  className="text-base text-green-600"
                  data-testid="text-corporate-additional-documents-sent"
                >
                  {t('corporateAdditionalDocuments.sent')}
                </p>
              )}

              {isError && (
                <span
                  className="text-sm text-red-500"
                  data-testid="error-corporate-additional-documents-submit"
                >
                  {t('corporateAdditionalDocuments.errors.submit')}
                </span>
              )}

              <Button
                onClick={handleSubmit}
                disabled={!hasFiles || isPending}
                className={clsx(
                  'inline-flex items-center py-8 px-4 w-full justify-center lg:gap-2',
                  (!hasFiles || isPending) &&
                    'opacity-50 cursor-not-allowed bg-gray-400'
                )}
                data-testid="btn-corporate-additional-documents-submit"
              >
                {isPending
                  ? t('corporateAdditionalDocuments.submitting')
                  : t('corporateAdditionalDocuments.submit')}
              </Button>

              <Link to={ROUTES.COMPLETE} className="underline text-sm">
                {t('corporateAdditionalDocuments.back')}
              </Link>
            </div>
          </CorporatePageLayout>
        </div>
      </div>
    )
  }
)
